import { Link } from "react-router-dom";
import { useState } from "react";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch("https://readdy.ai/api/form/d4i7pgttnoqs9bvma0a0", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", phone: "", company: "", message: "" });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-black/95 backdrop-blur-sm z-50 border-b border-yellow-600/20">
        <div className="px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/46562bb1bf2b4a38e1679072b6f45baa/5f0354f44941710a405b916e5b3e3bcf.jpeg" 
                alt="ROSPA WORLD PLUS ENTERPRISE Logo" 
                className="w-12 h-12 object-contain rounded-full"
              />
              <div>
                <h1 className="text-lg font-bold text-white leading-tight">
                  ROSPA WORLD PLUS <span className="text-yellow-500">ENTERPRISE</span>
                </h1>
                <p className="text-xs text-gray-400 italic">quality is our goal...</p>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-gray-200 hover:text-yellow-500 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
              <Link to="/#services" className="text-gray-200 hover:text-yellow-500 transition-colors whitespace-nowrap cursor-pointer">Services</Link>
              <Link to="/#products" className="text-gray-200 hover:text-yellow-500 transition-colors whitespace-nowrap cursor-pointer">Products</Link>
              <Link to="/#about" className="text-gray-200 hover:text-yellow-500 transition-colors whitespace-nowrap cursor-pointer">About</Link>
              <Link to="/contact" className="bg-yellow-600 text-black px-6 py-2 rounded-md hover:bg-yellow-500 transition-colors font-semibold whitespace-nowrap cursor-pointer">Contact Us</Link>
            </div>
            <button className="md:hidden text-yellow-500 w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Contact Section */}
      <section className="py-24 bg-gray-50 mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block bg-yellow-600/10 border border-yellow-600/30 px-4 py-2 rounded-full mb-4">
              <span className="text-yellow-700 font-semibold text-sm">Get In Touch</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Contact Us</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Ready to place an order or have questions? Reach out to us through any of the channels below. We're here to help!</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h4>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-yellow-700 text-2xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">Phone Numbers</h5>
                      <a href="tel:+233552121212" className="text-gray-600 hover:text-yellow-700 transition-colors block cursor-pointer">0552121212</a>
                      <a href="tel:+233509332793" className="text-gray-600 hover:text-yellow-700 transition-colors block cursor-pointer">0509332793</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-whatsapp-line text-green-700 text-2xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">WhatsApp</h5>
                      <a href="https://wa.me/233552121212" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-green-700 transition-colors block cursor-pointer">0552121212</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-yellow-700 text-2xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">Email</h5>
                      <a href="mailto:rospaworldplus30@gmail.com" className="text-gray-600 hover:text-yellow-700 transition-colors cursor-pointer break-all">rospaworldplus30@gmail.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-yellow-700 text-2xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">Address</h5>
                      <p className="text-gray-600">HQR6+VPG, Accra</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h5 className="font-semibold text-gray-900 mb-4">Quick Actions</h5>
                  <div className="flex flex-wrap gap-3">
                    <a href="tel:+233552121212" className="bg-yellow-600 text-black px-6 py-3 rounded-md hover:bg-yellow-500 transition-all font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
                      <i className="ri-phone-line w-5 h-5 flex items-center justify-center"></i>
                      Call Now
                    </a>
                    <a href="https://wa.me/233552121212" target="_blank" rel="noopener noreferrer" className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-500 transition-all font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
                      <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
                      WhatsApp Us
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h4>
                <form id="contact-form" onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-600 focus:border-transparent text-sm"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-600 focus:border-transparent text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-600 focus:border-transparent text-sm"
                      placeholder="Your phone number"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">Company Name</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-600 focus:border-transparent text-sm"
                      placeholder="Your company"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-600 focus:border-transparent text-sm resize-none"
                      placeholder="Tell us about your requirements..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>
                  {submitStatus === "success" && (
                    <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                      Thank you for your message! We'll get back to you soon.
                    </div>
                  )}
                  {submitStatus === "error" && (
                    <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                      Something went wrong. Please try again or contact us directly.
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-yellow-600 text-black py-4 rounded-md hover:bg-yellow-500 transition-all font-semibold text-lg whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Sending..." : "Get a Quote"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/46562bb1bf2b4a38e1679072b6f45baa/5f0354f44941710a405b916e5b3e3bcf.jpeg" 
                  alt="ROSPA WORLD PLUS ENTERPRISE Logo" 
                  className="w-12 h-12 object-contain rounded-full flex-shrink-0"
                />
                <div>
                  <h3 className="text-lg font-bold text-white leading-tight">ROSPA WORLD PLUS</h3>
                  <p className="text-yellow-500 font-semibold text-sm">ENTERPRISE</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 italic mb-4">quality is our goal...</p>
              <p className="text-gray-400 mb-6 leading-relaxed text-sm">Your trusted partner for safety products, mining consumables, plant and fire equipment, and general goods.</p>
              <div className="flex gap-4">
                <a href="https://www.facebook.com/Rospaworldplusenterprise" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-yellow-600 transition-colors cursor-pointer" aria-label="Facebook">
                  <i className="ri-facebook-fill text-xl w-5 h-5 flex items-center justify-center"></i>
                </a>
                <a href="https://www.instagram.com/_Rospaworld.plusenterprise23" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-yellow-600 transition-colors cursor-pointer" aria-label="Instagram">
                  <i className="ri-instagram-fill text-xl w-5 h-5 flex items-center justify-center"></i>
                </a>
                <a href="https://wa.me/233509332793" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors cursor-pointer" aria-label="WhatsApp">
                  <i className="ri-whatsapp-fill text-xl w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="hover:text-yellow-500 transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/#about" className="hover:text-yellow-500 transition-colors cursor-pointer">About Us</Link></li>
                <li><Link to="/#products" className="hover:text-yellow-500 transition-colors cursor-pointer">Products</Link></li>
                <li><Link to="/#services" className="hover:text-yellow-500 transition-colors cursor-pointer">Services</Link></li>
                <li><Link to="/contact" className="hover:text-yellow-500 transition-colors cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-4">Product Categories</h4>
              <ul className="space-y-3">
                <li><button className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Safety Equipment</button></li>
                <li><button className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Fire Equipment</button></li>
                <li><button className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Mining Consumables</button></li>
                <li><button className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Plant Equipment</button></li>
                <li><button className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Office Supplies</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-4">Contact Info</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-phone-line text-yellow-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <div>
                    <a href="tel:+233552121212" className="hover:text-yellow-500 transition-colors cursor-pointer block">0552121212</a>
                    <a href="tel:+233509332793" className="hover:text-yellow-500 transition-colors cursor-pointer block">0509332793</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-whatsapp-line text-green-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <a href="https://wa.me/233552121212" target="_blank" rel="noopener noreferrer" className="hover:text-green-500 transition-colors cursor-pointer">0552121212</a>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-mail-line text-yellow-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <a href="mailto:rospaworldplus30@gmail.com" className="hover:text-yellow-500 transition-colors cursor-pointer break-all">rospaworldplus30@gmail.com</a>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-yellow-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>HQR6+VPG, Accra</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm text-center md:text-left">© 2025 ROSPA WORLD PLUS ENTERPRISE. All rights reserved.</p>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-sm hover:text-yellow-500 transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
